import React from 'react';
import { HandIcon, LightbulbIcon, TimerIcon } from 'lucide-react';
const blocks = [{
  name: 'Warm-up',
  color: 'bg-cloud-dark',
  text: 'text-ink',
  body: 'Wrist circles, shoulder openers, spine rolls, light cardio',
  min: 10
}, {
  name: 'Condition',
  color: 'bg-amber',
  text: 'text-ink',
  body: 'Straight-arm hangs, scapular pulls, hollow holds on the floor',
  min: 10
}, {
  name: 'Skill',
  color: 'bg-coral',
  text: 'text-white',
  body: 'Foot wrap, basic climb, two climbs to a comfortable height',
  min: 25
}, {
  name: 'Sequence',
  color: 'bg-teal',
  text: 'text-white',
  body: 'Climb and controlled descent, then stretch forearms & shoulders',
  min: 15
}];
const ladder = [{
  dots: 1,
  level: 'Easier',
  body: 'Climb one arm-reach and step down'
}, {
  dots: 2,
  level: 'Target',
  body: 'Full basic climb to a comfortable height, controlled descent'
}, {
  dots: 3,
  level: 'Harder',
  body: 'Three continuous climbs'
}];
const anchor = [{
  label: 'Control',
  body: 'starts and finishes slowly, no swing, no snatch'
}, {
  label: 'Repeat',
  body: 'does it twice in a session without a reset'
}, {
  label: 'Breath',
  body: 'can talk or breathe easily while holding it'
}, {
  label: 'Recover',
  body: 'comes down under control, every time'
}];
function Dots({
  n
}: {
  n: number;
}) {
  return <span className="flex gap-0.5" aria-label={`${n} of 3`} data-id="element-296" data-mp="3h42r74gohvuq">
      {[1, 2, 3].map(i => <span key={i} className={`h-2 w-2 rounded-full ${i <= n ? 'bg-coral' : 'bg-cloud-dark'}`} data-id="element-297" data-mp="hoehaxjpveqs" />)}
    </span>;
}
function PageFrame({
  tag,
  title,
  band,
  children
}: {
  tag: string;
  title: string;
  band: string;
  children: React.ReactNode;
}) {
  return <article className="overflow-hidden rounded-2xl bg-cloud text-ink shadow-book" data-id="element-298" data-mp="7x1q885qsslt">
      <div className={`flex items-center justify-between px-5 py-2.5 text-white ${band}`} data-id="element-299" data-mp="3whp6ti1t4ue">
        <span className="text-[10px] font-bold uppercase tracking-[0.18em]" data-id="element-300" data-mp="10fyxt16krwnu">{tag}</span>
        <span className="text-[10px] font-semibold uppercase tracking-widest text-white/80" data-id="element-301" data-mp="vne6c6b55vnh">Vol. 2 · Silks</span>
      </div>
      <div className="p-5 sm:p-6" data-id="element-302" data-mp="2pwt24ryuaugu">
        <h3 className="font-display text-xl font-bold leading-tight tracking-tight sm:text-2xl" data-id="element-303" data-mp="2uve34tlx1i6c">{title}</h3>
        {children}
      </div>
    </article>;
}
export function SamplePlan() {
  return <section aria-labelledby="sample-heading" className="w-full bg-ink" data-id="element-304" data-mp="2l5krjslrx323">
      <div className="apparatus-strip h-1.5 w-full" aria-hidden="true" data-id="element-305" data-mp="2l3rhs4fv62pv" />
      <div className="mx-auto max-w-6xl px-5 py-16 sm:py-20" data-id="element-306" data-mp="27509wzrlhda6">
        <div className="text-center" data-id="element-307" data-mp="2c4mphfuwb5m5">
          <p className="text-[11px] font-semibold uppercase tracking-widest text-amber" data-id="element-308" data-mp="2c5ny7dofvg0p">Look inside</p>
          <h2 id="sample-heading" className="mt-3 font-display text-3xl font-extrabold text-white sm:text-5xl" data-id="element-309" data-mp="1x96oeqvwe9g9">
            One real class, straight from Volume 2
          </h2>
          <p className="mx-auto mt-4 max-w-2xl text-base text-white/75" data-id="element-310" data-mp="3rmpsykocisb2">
            Silks, Class 1: first climb &amp; safe dismount. The left page is the
            class you teach. The right page is how you know a student is ready for
            the next rung. Every one of the 60+ class plans works like this.
          </p>
        </div>

        <div className="mt-10 grid gap-5 lg:grid-cols-2" data-id="element-311" data-mp="1fw88c1r4tqcc">
          <PageFrame tag="Class Plan · Page 14" title="Class 1: First climb & safe dismount" band="bg-coral" data-id="element-312" data-mp="1uo8z017ei1n5">
            <dl className="mt-3 grid gap-1.5 text-[13px] text-slate" data-id="element-313" data-mp="1pvpd8gyk8079">
              <div data-id="element-314" data-mp="23nchb2yqud0l"><dt className="inline font-semibold text-ink" data-id="element-315" data-mp="23n47xl1txpu5">Level: </dt><dd className="inline" data-id="element-316" data-mp="23pqdxg92nxa9">1 dot · 45 / 60 / 75 min</dd></div>
              <div data-id="element-317" data-mp="1zlqy6zvm92hy"><dt className="inline font-semibold text-ink" data-id="element-318" data-mp="1zlr39eah3sot">Objective: </dt><dd className="inline" data-id="element-319" data-mp="2dmbjq3sx1cf5">every student makes one clean climb and comes down under control.</dd></div>
              <div data-id="element-320" data-mp="28o0bfxi9r9z1"><dt className="inline font-semibold text-ink" data-id="element-321" data-mp="28oa11jzejf47">Pre-flight: </dt><dd className="inline" data-id="element-322" data-mp="j7sg1xwn12vj">rig and carabiner checked, mat under each fabric, no jewellery, hair tied, ask who has a sore wrist or shoulder.</dd></div>
            </dl>
            <ul className="mt-4 grid gap-2" data-id="element-323" data-mp="ebaf794fhooh">
              {blocks.map(b => <li key={b.name} className="grid grid-cols-[76px_1fr_auto] items-start gap-3 rounded-xl border border-cloud-dark bg-white p-2.5" data-id="element-324" data-mp="fpv1colv4flj">
                  <span className={`rounded-lg px-2 py-1 text-center text-[10px] font-bold uppercase tracking-wider ${b.color} ${b.text}`} data-id="element-325" data-mp="pnr9r8nf0wfy">{b.name}</span>
                  <span className="text-[12.5px] leading-snug text-ink" data-id="element-326" data-mp="umlzyvnppna3">{b.body}</span>
                  <span className="flex items-center gap-1 text-[11px] font-bold text-slate" data-id="element-327" data-mp="3t6kldfwmvb7h">
                    <TimerIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-328" data-mp="1z80apsmqdma" />
                    {b.min}′
                  </span>
                </li>)}
            </ul>
            <p className="mt-4 text-[11px] font-bold uppercase tracking-widest text-coral-deep" data-id="element-329" data-mp="bpktt7if9h4k">Three levels, same class</p>
            <ol className="mt-2 grid gap-2" data-id="element-330" data-mp="3ae02mranemhe">
              {ladder.map(l => <li key={l.level} className="flex items-start gap-2.5 rounded-xl bg-white p-2.5" data-id="element-331" data-mp="3fcbet8of192s">
                  <span className="mt-1" data-id="element-332" data-mp="1wh6xvh1hbfby"><Dots n={l.dots} data-id="element-333" data-mp="1whp14xmk6ttd" /></span>
                  <span className="text-[12.5px] leading-snug text-slate" data-id="element-334" data-mp="1sgd96lg7pwi0">
                    <span className="font-bold uppercase tracking-wider text-ink" data-id="element-335" data-mp="1sf27qsa8p0u2">{l.level}: </span>
                    {l.body}
                  </span>
                </li>)}
            </ol>
          </PageFrame>

          <PageFrame tag="Framework · Page 12" title="Progress Anchor: when a student owns a skill" band="bg-teal" data-id="element-336" data-mp="1dk5rbrsa175w">
            <p className="mt-2 text-[13px] text-slate" data-id="element-337" data-mp="18ruq8llsvfbr">
              Owning is not “got it once.” Check four marks before you move a student up a rung of the Silks Flight Path.
            </p>
            <ol className="mt-4 grid grid-cols-2 gap-2" data-id="element-338" data-mp="zt0oyhxiy0rv">
              {anchor.map((a, i) => <li key={a.label} className="rounded-xl bg-white p-3" data-id="element-339" data-mp="ute9s2y4yagf" data-mpi={i}>
                  <span className="flex h-6 w-6 items-center justify-center rounded-full bg-coral font-display text-[12px] font-bold text-white" data-id="element-340" data-mp="pubeha7yjiln">{i + 1}</span>
                  <p className="mt-2 text-[12px] font-bold uppercase tracking-wider text-ink" data-id="element-341" data-mp="g54g4clhegcp">{a.label}</p>
                  <p className="mt-0.5 text-[12.5px] leading-snug text-slate" data-id="element-342" data-mp="l6tqdq0vqpzq">{a.body}</p>
                </li>)}
            </ol>
            <div className="mt-4 grid gap-2 text-[12.5px]" data-id="element-343" data-mp="b6t5w0r9y3ty">
              <p className="flex gap-2 text-ink" data-id="element-344" data-mp="b9fdg0e60jc6">
                <LightbulbIcon className="mt-0.5 h-4 w-4 flex-none text-amber-deep" aria-hidden="true" data-id="element-345" data-mp="ywnj4jfi9ueq" />
                <span data-id="element-346" data-mp="13uyrepq5jwuu"><span className="font-semibold" data-id="element-347" data-mp="13uys6rxz80vw">Top cue:</span> “Straight arms, legs do the work.”</span>
              </p>
              <p className="flex gap-2 text-ink" data-id="element-348" data-mp="q4d6kvh51cub">
                <span className="mt-0.5 flex h-4 w-4 flex-none items-center justify-center rounded-full bg-coral text-[9px] font-bold text-white" data-id="element-349" data-mp="u5oyx8recc67">!</span>
                <span data-id="element-350" data-mp="1huhzxjmvla7o"><span className="font-semibold" data-id="element-351" data-mp="1htooi5cz1wj7">#1 error → fix:</span> bent arms hauling the body up. Re-drill the foot wrap and let the legs press.</span>
              </p>
              <p className="flex gap-2 text-ink" data-id="element-352" data-mp="37blumz43kh6w">
                <HandIcon className="mt-0.5 h-4 w-4 flex-none text-teal-deep" aria-hidden="true" data-id="element-353" data-mp="32dtwvogru0us" />
                <span data-id="element-354" data-mp="3h2q8fvi1dw05"><span className="font-semibold" data-id="element-355" data-mp="3h2gig7wzroug">Recover always holds:</span> a student who can’t come down safely is not ready to go higher.</span>
              </p>
            </div>
          </PageFrame>
        </div>

        <p className="mt-6 text-center text-[13px] text-white/60" data-id="element-356" data-mp="2od9akibndmwj">
          Volume 2 of 6 · 12 class plans + 4 worksheets + reference pages and a printable field card
        </p>
      </div>
    </section>;
}